import { useEffect } from 'react';
import {
  Percent_Hours_To_Detect_After,
  Business_Hours_PerYr_PerEngr,
  Avg_Salary,
} from '../constants/spread';

/**
 * Calculate the estimated price
 *
 * @param {object} intl used for the formatting
 * @param {string} lang the language code to get the currency sign for
 * @param {number} dataEngineers : Number of data engineeres and employees that work on detecting and resolving pipeline/data issues
 * @param {number} meanTimeToDetect : Percent of time engineers spend finding pipeline/data issues
 * @param {boolean} meanTimeToResolve : Percent of time engineers spend fixing pipeline/data issues
 * @param {string} focusLevel : Focus level on data observability initiatives in next 12 months
 * @returns {Array}
 */

export interface PropsFunc {
  dataEngineers: number;
  meanTimeToDetect: number;
  meanTimeToResolve: number;
  focusLevel: string;
  setHoursSaving: React.Dispatch<React.SetStateAction<number>>;
  setYearlySaving: React.Dispatch<React.SetStateAction<number>>;
  setThreeYearSaving: React.Dispatch<React.SetStateAction<number>>;
}

export interface IFetchError {
  status: number;
  statusText: string;
}

export const usePrice = ({
  dataEngineers,
  meanTimeToDetect,
  meanTimeToResolve,
  focusLevel,
  setHoursSaving,
  setYearlySaving,
  setThreeYearSaving,
}: PropsFunc) => {
  useEffect(() => {
    const hoursSaved = [];
    hoursSaved.push(determine_hoursSavedToDetect());
    hoursSaved.push(determine_hoursSavedToResolve());
    hoursSaved.push(determine_hoursSavedCustomLogic());
    const totalHoursSaved = hoursSaved[0] + hoursSaved[1] + hoursSaved[2];
    setHoursSaving(totalHoursSaved);
    const yearlySaving = Math.round((Avg_Salary / 52 / 40) * totalHoursSaved);
    setYearlySaving(yearlySaving);
    setThreeYearSaving(Math.round(yearlySaving * 3));
  }, [dataEngineers, meanTimeToDetect, meanTimeToResolve, focusLevel]);

  const determine_hoursSavedToDetect = () => {
    const hoursToDetectBefore = Math.round(
      dataEngineers * Business_Hours_PerYr_PerEngr * (meanTimeToDetect / 100),
    );
    const hoursToDetectAfter = Math.round(
      (Percent_Hours_To_Detect_After / 100) *
        Business_Hours_PerYr_PerEngr *
        dataEngineers,
    );
    const hoursSavedToDetectPerYear = hoursToDetectBefore - hoursToDetectAfter;
    return hoursSavedToDetectPerYear;
  };

  const determine_hoursSavedToResolve = () => {
    const percentHoursToResolveAfter = meanTimeToResolve / 2;
    const hoursToResolveBefore = Math.round(
      dataEngineers * Business_Hours_PerYr_PerEngr * (meanTimeToResolve / 100),
    );
    const hoursToResolveAfter = Math.round(
      dataEngineers *
        Business_Hours_PerYr_PerEngr *
        (percentHoursToResolveAfter / 100),
    );
    const hoursSavedToResolvePerYear =
      hoursToResolveBefore - hoursToResolveAfter;
    return hoursSavedToResolvePerYear;
  };

  const determine_hoursSavedCustomLogic = () => {
    let hoursSavedCustomLogic = 0;
    if (focusLevel === 'Low')
      hoursSavedCustomLogic = Math.round(
        dataEngineers * Business_Hours_PerYr_PerEngr * 0.05,
      );
    if (focusLevel === 'Medium')
      hoursSavedCustomLogic = Math.round(
        dataEngineers * Business_Hours_PerYr_PerEngr * 0.15,
      );
    if (focusLevel === 'High')
      hoursSavedCustomLogic = Math.round(
        dataEngineers * Business_Hours_PerYr_PerEngr * 0.3,
      );
    if (focusLevel === 'None')
      hoursSavedCustomLogic = Math.round(
        dataEngineers * Business_Hours_PerYr_PerEngr * 0,
      );
    return hoursSavedCustomLogic;
  };
};
